import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { UserCircle2, Lock, EyeOff, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import BrandMark from "@/components/BrandMark";
import DisclaimerBanner from "@/components/DisclaimerBanner";

function getSubmitLabel(loading, mode) {
  if (loading) return "Procesando…";
  return mode === "login" ? "Entrar" : "Crear cuenta anónima";
}

const SUGGESTIONS = ["Aguila Plata", "Sol Sereno", "Tigre Azul", "Luna Roja", "Viento del Norte", "Colibri Veloz", "Coyote Libre", "Estrella Negra"];

export default function Auth() {
  const navigate = useNavigate();
  const { user, login, register, loading } = useAuth();
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Auto-redirect when auth state is committed
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const suggestPseudonym = () => {
    const s = SUGGESTIONS[Math.floor(Math.random() * SUGGESTIONS.length)];
    setUsername(s + " " + Math.floor(Math.random() * 999));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Ingresa pseudónimo y contraseña");
      return;
    }
    try {
      if (mode === "login") {
        await login(username, password);
      } else {
        await register(username, password);
      }
      // navigation happens via useEffect when user state commits
    } catch (err) {
      const msg = err.response?.data?.detail || "Error en la autenticación";
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <div className="max-w-md mx-auto w-full px-5 pt-6">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 mb-4 hover:text-zinc-900"
          data-testid="auth-back-landing"
        >
          <ArrowLeft className="w-4 h-4" />
          Inicio
        </button>

        <div className="flex flex-col items-center mb-6">
          <BrandMark size="md" />
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 text-center mt-6">
            Tu identidad está protegida
          </h1>
          <p className="text-sm text-zinc-600 text-center mt-2 leading-snug max-w-sm">
            No pedimos nombre real, teléfono ni dirección. Solo un pseudónimo y una contraseña.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-6">
          <div className="grid grid-cols-2 gap-2 mb-6 p-1 bg-zinc-100 rounded-xl">
            <button
              data-testid={AUTH.modeLogin}
              onClick={() => setMode("login")}
              className={`h-11 rounded-lg font-bold transition-all ${
                mode === "login" ? "bg-white text-zinc-900 shadow" : "text-zinc-500"
              }`}
            >
              Entrar
            </button>
            <button
              data-testid={AUTH.modeRegister}
              onClick={() => setMode("register")}
              className={`h-11 rounded-lg font-bold transition-all ${
                mode === "register" ? "bg-white text-zinc-900 shadow" : "text-zinc-500"
              }`}
            >
              Crear cuenta
            </button>
          </div>

          <form onSubmit={submit} className="flex flex-col gap-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-base font-semibold text-zinc-900 flex items-center gap-2">
                  <UserCircle2 className="w-4 h-4" />
                  Pseudónimo
                </Label>
                {mode === "register" && (
                  <button
                    type="button"
                    onClick={suggestPseudonym}
                    className="text-xs font-bold text-blue-800 hover:underline flex items-center gap-1"
                    data-testid="auth-suggest-pseudonym"
                  >
                    <Sparkles className="w-3 h-3" />
                    Sugerir
                  </button>
                )}
              </div>
              <Input
                data-testid={AUTH.inputName}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={mode === "register" ? "Inventa un nombre, ej: Aguila Plata" : "Tu pseudónimo"}
                className="h-14 text-base rounded-xl border-2 border-zinc-300 focus:border-blue-800"
              />
              {mode === "register" && (
                <p className="text-xs text-zinc-500 mt-1.5 leading-snug">
                  Inventa un nombre. NO uses tu nombre real para proteger tu integridad migratoria.
                </p>
              )}
            </div>

            <div>
              <Label className="text-base font-semibold text-zinc-900 mb-1.5 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Contraseña
              </Label>
              <Input
                data-testid={AUTH.inputPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="h-14 text-base rounded-xl border-2 border-zinc-300 focus:border-blue-800"
              />
            </div>

            <Button
              data-testid={AUTH.submit}
              type="submit"
              disabled={loading}
              className="h-16 text-lg font-bold bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl mt-2"
            >
              {getSubmitLabel(loading, mode)}
            </Button>
          </form>

          <div className="mt-5 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <EyeOff className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-emerald-900 leading-snug">
              <span className="font-bold">100% anónimo.</span> No guardamos su nombre real, dirección, ni Número A. Las transcripciones de sus llamadas se guardan solo <span className="font-bold">30 días</span> para que la IA pueda recordar entre sesiones, luego se borran automáticamente. Usted también puede borrarlas en cualquier momento desde su panel.
            </p>
          </div>
        </div>

        <p className="text-xs text-zinc-500 text-center mt-6 leading-relaxed px-4">
          Esta plataforma es informativa y educacional. No prestamos servicios legales ni sustituimos a un abogado.
        </p>
      </div>
      <DisclaimerBanner />
    </div>
  );
}
